import { useState } from 'react';
import { Loader2, Play, RefreshCw, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function OBSyncRunNowPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.obSyncRunNow();
      setResult(data);
      if (data.rate_limited) {
        toast.warning('OB sync hit rate limit (429)');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const hasErrors = result?.results?.some(r => r.error || r.errors?.length > 0);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-teal-600" />
            OB Sync Run-Now
          </CardTitle>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Sync Now
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Trigger one OB sync pass on demand. Syncs all connected bank connections (accounts + transactions). Use this instead of waiting for the hourly scheduler.
        </p>
      </CardHeader>
      {result && (
        <CardContent className="space-y-4">
          {/* Summary banner */}
          <div className={`border rounded-lg p-3 ${
            !hasErrors && !result.rate_limited
              ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
              : 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800'
          }`}>
            <div className="flex items-center gap-2 text-sm font-medium">
              {!hasErrors && !result.rate_limited ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-green-700 dark:text-green-400">
                    Sync completed
                  </span>
                </>
              ) : result.rate_limited ? (
                <>
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="text-amber-700 dark:text-amber-400">
                    Sync hit rate limit (429). Try again later or after a Connect with X-PSU-IP.
                  </span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="text-amber-700 dark:text-amber-400">
                    Sync completed with errors (see details below)
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="border rounded-lg p-3 text-center">
              <div className="text-2xl font-bold">{result.total_connections ?? 0}</div>
              <div className="text-xs text-muted-foreground">Connections</div>
            </div>
            <div className="border rounded-lg p-3 text-center">
              <div className="text-2xl font-bold">{result.accounts_synced ?? 0}</div>
              <div className="text-xs text-muted-foreground">Accounts</div>
            </div>
            <div className="border rounded-lg p-3 text-center">
              <div className="text-2xl font-bold">{result.transactions_synced ?? 0}</div>
              <div className="text-xs text-muted-foreground">Transactions</div>
            </div>
          </div>

          {/* No connections message */}
          {result.message && (
            <div className="text-sm text-muted-foreground">{result.message}</div>
          )}

          {/* Per-connection results */}
          {result.results?.length > 0 && (
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                Per-Connection Results
              </h4>
              <div className="space-y-2">
                {result.results.map((r, i) => (
                  <div key={i} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      {r.error ? (
                        <Badge variant="destructive" className="gap-1 text-[10px]">
                          <XCircle className="h-3 w-3" />
                          ERROR
                        </Badge>
                      ) : r.rate_limited ? (
                        <Badge className="bg-amber-600 hover:bg-amber-700 text-white gap-1 text-[10px]">
                          <AlertTriangle className="h-3 w-3" />
                          429
                        </Badge>
                      ) : (
                        <Badge className="bg-green-600 hover:bg-green-700 text-white gap-1 text-[10px]">
                          <CheckCircle2 className="h-3 w-3" />
                          OK
                        </Badge>
                      )}
                      <span className="text-sm font-medium font-mono">
                        {r.connection_id || `Connection ${i + 1}`}
                      </span>
                      {!r.error && (
                        <span className="text-xs text-muted-foreground">
                          {r.accounts_synced ?? 0} accts, {r.transactions_synced ?? 0} txs
                        </span>
                      )}
                    </div>
                    {r.error && (
                      <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {r.error}
                      </div>
                    )}
                    {r.errors?.length > 0 && (
                      <pre className="mt-1 p-2 bg-muted rounded text-[10px] overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(r.errors, null, 2)}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.checked_at && (
            <p className="text-[10px] text-muted-foreground text-right">
              Synced at {new Date(result.checked_at).toLocaleString()}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
